import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Engagement } from 'sr-types/lib/search/v1/graphql';

interface EngagementTrackerContextValue {
    engagements: Engagement[];
    clickedEngagement: Engagement | null;
    isLoadingEngagements: boolean;
    errorLoadingEngagements: Error | null;
    clickedPerson: string | null;
    setClickedPerson: (personId: string | null) => void;
    setEngagements: (engagements: Engagement[]) => void;
    setClickedEngagement: (engagement: Engagement | null) => void;
    setIsLoadingEngagements: (loading: boolean) => void;
    setErrorLoadingEngagements: (error: Error | null) => void;
    refetchEngagements: () => Promise<void>;
    registerRefetch: (refetchFn: () => Promise<void>) => void;
}

const EngagementTrackerContext = createContext<EngagementTrackerContextValue | undefined>(undefined);

interface EngagementTrackerProviderProps {
    children: ReactNode;
}

export function EngagementTrackerProvider({ children }: EngagementTrackerProviderProps) {
    const [engagements, setEngagements] = useState<Engagement[]>([]);
    const [clickedEngagement, setClickedEngagement] = useState<Engagement | null>(null);
    const [clickedPerson, setClickedPerson] = useState<string | null>(null);
    const [isLoadingEngagements, setIsLoadingEngagements] = useState(false);
    const [errorLoadingEngagements, setErrorLoadingEngagements] = useState<Error | null>(null);
    const refetchFnRef = useRef<(() => Promise<void>) | null>(null);

    const registerRefetch = useCallback((refetchFn: () => Promise<void>) => {
        refetchFnRef.current = refetchFn;
    }, []);

    const refetchEngagements = useCallback(async () => {
        if (!refetchFnRef.current) {
            console.warn('Refetch function not registered');
            return;
        }

        setIsLoadingEngagements(true);
        setErrorLoadingEngagements(null);

        try {
            await refetchFnRef.current();
        } catch (error) {
            const err = error instanceof Error ? error : new Error('Failed to refetch engagements');
            setErrorLoadingEngagements(err);
        } finally {
            setIsLoadingEngagements(false);
        }
    }, []);

    const value: EngagementTrackerContextValue = useMemo(
        () => ({
            engagements,
            clickedEngagement,
            clickedPerson,
            setClickedEngagement,
            setClickedPerson,
            isLoadingEngagements,
            errorLoadingEngagements,
            setEngagements,
            setIsLoadingEngagements,
            setErrorLoadingEngagements,
            refetchEngagements,
            registerRefetch
        }),
        [
            engagements,
            clickedPerson,
            setClickedPerson,
            clickedEngagement,
            isLoadingEngagements,
            errorLoadingEngagements,
            refetchEngagements,
            registerRefetch
        ]
    );

    return <EngagementTrackerContext.Provider value={value}>{children}</EngagementTrackerContext.Provider>;
}

export function useEngagementTracker() {
    const context = useContext(EngagementTrackerContext);

    if (context === undefined) {
        throw new Error('useEngagementTracker must be used within an EngagementTrackerProvider');
    }

    return context;
}
